import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Divider,
  CircularProgress,
  alpha,
  useTheme,
  SelectChangeEvent,
  Autocomplete,
  InputAdornment,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  LocalAtm as LocalAtmIcon,
  ReceiptLong as ReceiptLongIcon,
  FilterAlt as FilterAltIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Description as DescriptionIcon,
  DateRange as DateRangeIcon,
  ViewQuilt as ViewQuiltIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { getTransactions, getAccounts, Transaction as TransactionType, Account } from '../../../services/financeService';

// تعريف واجهة الحساب للاختيار
interface AccountOption {
  id: string;
  accountNumber: string;
  name: string;
  label: string;
  isReal?: boolean;
  isGeneralAccount?: boolean;
}

// لا نستخدم أي بيانات وهمية للعرض في هذه الصفحة

const TransactionsList: React.FC = () => {
  const theme = useTheme();
  
  // حالة البيانات والفلاتر
  const [transactions, setTransactions] = useState<TransactionType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAccount, setSelectedAccount] = useState<AccountOption | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [transactionType, setTransactionType] = useState<string>('');
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  
  // متغيرات التصفيح
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalTransactions, setTotalTransactions] = useState(0);
  
  // تحميل البيانات الحسابات
  const fetchAccounts = async () => {
    try {
      console.log('Fetching accounts...');
      const response = await getAccounts();
      console.log('Accounts received:', response);
      
      // تحويل الحسابات إلى تنسيق الاختيار
      const accountOptions: AccountOption[] = response.map(account => ({
        id: account.id,
        accountNumber: account.accountNumber,
        name: account.name,
        label: `${account.accountNumber} - ${account.name}`,
        isReal: account.isReal,
        isGeneralAccount: account.isGeneralAccount
      }));
      setAccounts(accountOptions);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setErrorMessage('حدث خطأ أثناء تحميل بيانات الحسابات');
    }
  };
  
  // تحميل البيانات عند تهيئة المكون
  useEffect(() => {
    fetchAccounts();
  }, []);
  
  // تحميل المعاملات عند تغيير pagination أو filters
  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, selectedAccount, startDate, endDate, transactionType, searchTerm]);
  
  // إعادة تعيين الصفحة عند تغيير الفلاتر
  useEffect(() => {
    setPage(0);
  }, [selectedAccount, startDate, endDate, transactionType, searchTerm]);

  // تحميل المعاملات
  const fetchTransactions = async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      // إضافة المعلمات مباشرة إلى طلب getTransactions لتصفية البيانات من جانب الخادم
      const params = {
        showRealOnly: true, // دائمًا عرض الحسابات الحقيقية في الحسابات العامة فقط
        accountId: selectedAccount?.id,
        startDate,
        endDate,
        transactionType,
        search: searchTerm,
        page: page + 1,
        limit: rowsPerPage
      };
      
      console.log('Fetching transactions with params:', params);
      const response = await getTransactions(params);
      
      console.log('Received response:', response);
      
      // التعامل مع نوعي الاستجابة (مصفوفة أو كائن)
      if (Array.isArray(response)) {
        // تنسيق قديم: استجابة هي مصفوفة مباشرة من المعاملات
        setTransactions(response);
        setTotalTransactions(response.length);
        
        if (response.length === 0) {
          setErrorMessage('لا توجد معاملات مطابقة للمعايير المحددة');
        } else {
          // تحليل البيانات لأغراض التصحيح
          const realTxs = response.filter(tx => tx.isReal === true);
          const virtualTxs = response.filter(tx => tx.isReal !== true);
          
          console.log('Debug info:', {
            totalTransactions: response.length,
            realTransactions: realTxs.length,
            virtualTransactions: virtualTxs.length
          });
        }
      } else if (response && 'transactions' in response) {
        // تنسيق جديد: استجابة هي كائن يحتوي على مصفوفة المعاملات
        const txResponse = response;
        setTransactions(txResponse.transactions);
        setTotalTransactions((txResponse as any).total || txResponse.transactions.length);
        
        if (txResponse.transactions.length === 0) {
          // عرض الرسالة المخصصة من الخادم إذا كانت متوفرة
          if (txResponse.message) {
            setErrorMessage(txResponse.message);
          } else {
            setErrorMessage('لا توجد معاملات مطابقة للمعايير المحددة');
          }
        } else {
          // تحليل البيانات لأغراض التصحيح
          const realTxs = txResponse.transactions.filter(tx => tx.isReal === true);
          const virtualTxs = txResponse.transactions.filter(tx => tx.isReal !== true);
          
          console.log('Debug info:', {
            totalTransactions: txResponse.transactions.length,
            realTransactions: realTxs.length,
            virtualTransactions: virtualTxs.length,
            message: txResponse.message,
            code: txResponse.code
          });
        }
      } else {
        // استجابة غير متوقعة
        console.error('Unexpected response format:', response);
        setErrorMessage('تنسيق استجابة غير متوقع من الخادم');
        setTransactions([]);
        setTotalTransactions(0);
      }
    } catch (error: any) {
      console.error('Error fetching transactions:', error);
      
      // استخراج رسالة الخطأ من الاستجابة إذا كانت متوفرة
      let errorMsg = 'حدث خطأ أثناء تحميل بيانات المعاملات';
      
      if (error.response && error.response.data) {
        if (error.response.data.message) {
          errorMsg = error.response.data.message;
        }
        
        if (error.response.data.code) {
          console.log('Error code:', error.response.data.code);
          
          if (error.response.data.code === 'NO_REAL_ACCOUNTS') {
            errorMsg = 'لا توجد حسابات حقيقية في قسم الحسابات العامة. يرجى إضافة حساب حقيقي أولاً.';
          }
        }
      }
      
      setErrorMessage(errorMsg);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };
  
  // معالجة البحث والتصفية - تم تعديله لإرسال الطلب إلى الخادم بدلاً من التصفية المحلية
  const handleSearch = () => {
    // نقوم بتنفيذ استعلام جديد بالفلاتر الحالية
    fetchTransactions();
  };
  
  // معالجة تغيير الفلاتر
  const handleAccountChange = (event: React.SyntheticEvent, value: AccountOption | null) => {
    setSelectedAccount(value);
  };
  
  const handleTransactionTypeChange = (event: SelectChangeEvent) => {
    setTransactionType(event.target.value);
  };
  
  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value);
  };
  
  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
  };
  
  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedAccount(null);
    setStartDate('');
    setEndDate('');
    setTransactionType('');
    setPage(0); // إعادة تعيين الصفحة عند إعادة تعيين الفلاتر
    // دائماً عرض الحسابات الحقيقية في الحسابات العامة
  };
  
  // دوال التصفيح
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // في Server-side Pagination، نستخدم transactions مباشرة من API
  // لأن الخادم يرسل فقط المعاملات الخاصة بالصفحة الحالية
  
  // عرض نوع المعاملة
  const renderReferenceTypeChip = (type: 'سند قبض' | 'سند صرف' | 'قيد يومية') => {
    let color: 'success' | 'error' | 'info' = 'info';
    let variant: 'filled' | 'outlined' = 'filled';
    
    if (type === 'سند قبض') {
      color = 'success';
    } else if (type === 'سند صرف') {
      color = 'error';
    }
    
    return (
      <Chip 
        size="small" 
        label={type} 
        color={color}
        variant={variant}
        sx={{ 
          fontWeight: 500,
          fontSize: '0.75rem'
        }}
      />
    );
  };
  
  // تنسيق تاريخ المعاملة
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  return (
    <Box sx={{ mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          border: '1px solid',
          borderColor: alpha('#000', 0.08),
          background: 'white'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3 
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
            <AccountBalanceWalletIcon sx={{ mr: 1, color: '#30c3b0' }} />
            الأرصدة والمعاملات
          </Typography>
        </Box>
        
        {errorMessage && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setErrorMessage('')}
          >
            {errorMessage}
          </Alert>
        )}
        
        <Divider sx={{ my: 2 }} />
        
        {/* فلاتر البحث */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                placeholder="بحث عن معاملة..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Autocomplete
                size="small"
                options={accounts}
                getOptionLabel={(option) => option.label}
                value={selectedAccount}
                onChange={handleAccountChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="الحساب"
                    placeholder="اختر حساب"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <ViewQuiltIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>نوع المعاملة</InputLabel>
                <Select
                  value={transactionType}
                  onChange={handleTransactionTypeChange}
                  label="نوع المعاملة"
                  displayEmpty
                  startAdornment={
                    <InputAdornment position="start">
                      <DescriptionIcon fontSize="small" sx={{ color: 'text.secondary', ml: 1 }} />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="">الكل</MenuItem>
                  <MenuItem value="سند قبض">سند قبض</MenuItem>
                  <MenuItem value="سند صرف">سند صرف</MenuItem>
                  <MenuItem value="قيد يومية">قيد يومية</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                label="من تاريخ"
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DateRangeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                label="إلى تاريخ"
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DateRangeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleResetFilters}
                sx={{ flexGrow: 1 }}
                size="small"
              >
                إعادة تعيين
              </Button>
              
              <Button
                variant="contained"
                startIcon={<FilterAltIcon />}
                onClick={handleSearch}
                sx={{
                  flexGrow: 2,
                  bgcolor: '#30c3b0',
                  '&:hover': { bgcolor: '#25a899' },
                }}
                size="small"
              >
                تطبيق الفلتر
              </Button>
            </Grid>
          </Grid>
        </Box>
        
        {/* جدول المعاملات */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={40} sx={{ color: '#30c3b0' }} />
          </Box>
        ) : (
          <>
            {transactions.length > 0 ? (
              <>
                <TableContainer component={Box}>
                  <Table sx={{ minWidth: 800 }}>
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                        <TableCell>التاريخ</TableCell>
                        <TableCell>الرقم المرجعي</TableCell>
                        <TableCell>النوع</TableCell>
                        <TableCell>البيان</TableCell>
                        <TableCell>الحساب</TableCell>
                        <TableCell align="left">مدين</TableCell>
                        <TableCell align="left">دائن</TableCell>
                        <TableCell align="left">الرصيد</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((transaction) => (
                        <TableRow
                          key={transaction.id}
                          hover
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 }
                          }}
                        >
                          <TableCell>{formatDate(transaction.date)}</TableCell>
                          <TableCell>
                            <Tooltip title={`فتح ${transaction.referenceType}`} arrow>
                              <Link
                                to={`/finance/${
                                  transaction.referenceType === 'سند قبض'
                                    ? 'receipts'
                                    : transaction.referenceType === 'سند صرف'
                                    ? 'payments'
                                    : 'ledger'
                                }/view/${transaction.referenceNumber}`}
                                style={{ textDecoration: 'none', color: theme.palette.primary.main, fontWeight: 500 }}
                              >
                                {transaction.referenceNumber}
                              </Link>
                            </Tooltip>
                          </TableCell>
                          <TableCell>{renderReferenceTypeChip(transaction.referenceType)}</TableCell>
                          <TableCell>{transaction.description}</TableCell>
                          <TableCell>
                            <Tooltip title={`عرض حساب ${transaction.accountName}`} arrow>
                              <Link
                                to={`/finance/accounts/view/${transaction.accountId}`}
                                style={{ textDecoration: 'none', color: theme.palette.text.primary }}
                              >
                                {transaction.accountNumber} - {transaction.accountName}
                              </Link>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{
                              color: transaction.debit > 0 ? theme.palette.success.main : 'inherit',
                              fontWeight: transaction.debit > 0 ? 600 : 'inherit'
                            }}
                          >
                            {transaction.debit > 0 ? transaction.debit.toLocaleString() : '-'}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{
                              color: transaction.credit > 0 ? theme.palette.error.main : 'inherit',
                              fontWeight: transaction.credit > 0 ? 600 : 'inherit'
                            }}
                          >
                            {transaction.credit > 0 ? transaction.credit.toLocaleString() : '-'}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                          >
                            {transaction.balance.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* إضافة شريط التصفيح */}
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  component="div"
                  count={totalTransactions}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="عدد المعاملات في الصفحة:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} من ${count}`
                  }
                  sx={{
                    mt: 2,
                    '& .MuiTablePagination-toolbar': {
                      flexWrap: 'wrap',
                      '& .MuiTablePagination-spacer': { flex: '0 1 auto' }
                    }
                  }}
                />
              </>
            ) : (
              <Box sx={{
                py: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                opacity: 0.8
              }}>
                <ReceiptLongIcon sx={{ fontSize: 60, color: alpha(theme.palette.primary.main, 0.3), mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  لا توجد معاملات مطابقة
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {errorMessage || "تأكد من وجود حسابات حقيقية مضافة للحسابات العامة ومعاملات مسجلة عليها"}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={fetchTransactions}
                  sx={{ mt: 2 }}
                  startIcon={<RefreshIcon />}
                  size="small"
                >
                  إعادة المحاولة
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default TransactionsList;